import { useLocation } from 'react-router-dom';

import { AlreadyLoggedIn } from '../../components/auth-layout/AlreadyLoggedIn';
import { AuthLayout } from '../../components/auth-layout/AuthLayout';
import { MfaStep } from '../../components/auth-layout/MfaStep';
import { Spinner } from '../../components/ui/spinner';
import { COINIS_LOGO_DARK } from '../../config';
import { usePreexistingSession } from '../../utils/hooks/usePreexistingSession';
import { LinkError } from './components/LinkError';
import { PasswordSetupForm } from './components/PasswordSetupForm';
import { DangerTriangle } from './components/svg/DangerTriangle';
import { MailOutlined } from './components/svg/MailOutlined';
import { useSetupFlow } from './hooks/useSetupFlow';

export const UserSetup = (): JSX.Element => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const redirectUrl = decodeURIComponent(params.get('redirect_url') ?? '/');
  const signedInElsewhere = usePreexistingSession();

  const {
    loading,
    setupLoading,
    userDetails,
    mfaLink,
    error,
    currentStep,
    loadingState,
    handlePasswordSetup,
    handleMfaVerify,
    setCurrentStep
  } = useSetupFlow(token, redirectUrl, signedInElsewhere);

  // Setup links are emailed and land in whatever browser the person happens to
  // be using — often one that already holds a session for another account. Tell
  // them why the link looks dead instead of bouncing them to the dashboard. This
  // comes first: the token has deliberately not been fetched yet, so every
  // branch below would otherwise read as a broken link.
  if (signedInElsewhere) {
    return (
      <AuthLayout>
        <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />
        <AlreadyLoggedIn description="This setup link is for a different account, or for one that has already been set up. Log out of your current session to continue — you'll come right back to this page." />
      </AuthLayout>
    );
  }

  // A failed initial load (token invalid, expired, or replaced by a newer
  // verification email) must show the recovery UI — NOT the spinner. This has
  // to be checked before the loading guard below, because on error `loading`
  // is false but `userDetails` stays null, so `!userDetails` would otherwise
  // keep the spinner up forever.
  if (error && !userDetails) {
    const expired = error.statusCode === 400;
    return (
      <AuthLayout>
        <LinkError
          circleClassName={expired ? 'bg-red-100' : 'bg-brand-blue/10'}
          icon={expired ? <MailOutlined /> : <DangerTriangle />}
          title={expired ? 'Setup link expired' : 'This link is no longer valid'}
          description={
            expired
              ? 'The setup link for your Coinis account has expired. To continue setting up your account, request a new link below.'
              : 'This setup link is no longer valid — it may have already been used, revoked, or replaced by a newer verification email. Request a new link below.'
          }
        />
      </AuthLayout>
    );
  }

  if (loading || !userDetails) {
    return (
      <AuthLayout>
        <div className="flex min-h-[40vh] flex-col items-center justify-center">
          <Spinner size="lg" centered />
        </div>
      </AuthLayout>
    );
  }

  const showPasswordOption = userDetails.authType === null || userDetails.authType === 'password';

  // Only show validation errors (422) from form submission
  const validationError = error?.statusCode === 422 && error.message ? error.message : null;

  if (currentStep === 'mfa-qr' || currentStep === 'mfa-code') {
    return (
      <AuthLayout>
        <MfaStep
          variant={currentStep === 'mfa-qr' ? 'qr' : 'code'}
          mfaLink={mfaLink ?? undefined}
          email={userDetails.email}
          isLoading={setupLoading}
          error={error?.message}
          onComplete={handleMfaVerify}
          onBack={() => setCurrentStep('setup-form')}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {loadingState !== 'redirecting' && (
        <>
          <img src={COINIS_LOGO_DARK} alt="Coinis" className="h-7 w-auto" />
          <div className="flex flex-col gap-[11px]">
            <h1 className="font-onboarding-1 text-[1.625rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary">
              Finish setting up your account
            </h1>
            <p className="text-[15px] leading-[1.5] text-[var(--ink-2)]">
              Just your name and a password — we&apos;ve prepped the rest.
            </p>
          </div>

          {currentStep === 'setup-form' && showPasswordOption && (
            <PasswordSetupForm onSubmit={handlePasswordSetup} loading={setupLoading} error={validationError} />
          )}
          <p className="text-center text-[11.5px] leading-[1.5] text-[var(--ink-3)]">
            By continuing you agree to our{' '}
            <a
              href="https://coinis.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-[var(--ink-2)] underline underline-offset-2"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://coinis.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-[var(--ink-2)] underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </p>
        </>
      )}
    </AuthLayout>
  );
};
